import { useEffect, useRef, useState } from "react";
import { getEntitlementsAsync } from "../api/SubscriptionApi";
import { OrgEntitlements } from "../models/SubscriptionModels";
import { Constants } from "../constants/constants";

interface UseOrgEntitlementsMapReturn {
  map: Record<string, OrgEntitlements>;
  loading: boolean;
  error: string | null;
}

// Fetches entitlements for a set of organizations (e.g. one paginated page),
// caching by organization id so revisiting a page is free. Use only for
// bounded lists — prefer the detail page for ad-hoc lookups.
export const useOrgEntitlementsMap = (
  orgIds: string[],
): UseOrgEntitlementsMapReturn => {
  const [map, setMap] = useState<Record<string, OrgEntitlements>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cacheRef = useRef<Record<string, OrgEntitlements>>({});

  useEffect(() => {
    if (orgIds.length === 0) {
      setMap({});
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

    Promise.all(
      orgIds.map(async (id) => {
        if (cacheRef.current[id]) return { id, ent: cacheRef.current[id] };
        const res = await getEntitlementsAsync(token, id);
        return { id, ent: res.success ? (res.result ?? null) : null };
      }),
    )
      .then((results) => {
        if (cancelled) return;
        const next: Record<string, OrgEntitlements> = {};
        results.forEach(({ id, ent }) => {
          if (ent) {
            next[id] = ent;
            cacheRef.current[id] = ent;
          }
        });
        setMap(next);
        setError(null);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load subscription status");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orgIds]);

  return { map, loading, error };
};